import type { Currency, ISODate } from "../domain";
import { compareISODate, parseISODate } from "../domain";
import { mean, sampleStandardDeviation } from "./math";

export interface ActualBacktestObservation {
  readonly date: ISODate;
  readonly legAPrice: number;
  readonly legBPrice: number;
  readonly legAFxToCommon?: number;
  readonly legBFxToCommon?: number;
  readonly signal: number | null;
  readonly signalAsOf: ISODate;
  readonly beta?: number;
  readonly eligible?: boolean;
  readonly isForwardFilled?: boolean;
  readonly baselineContractYears?: readonly number[];
}

export interface ActualBacktestEpisode {
  readonly kind: "actual-episode";
  readonly source: "actual-settlements";
  readonly backAdjusted: false;
  readonly episodeId: string;
  readonly contractYear: number;
  readonly legAContract: string;
  readonly legBContract: string;
  readonly observations: readonly ActualBacktestObservation[];
}

export type ContractRounding = "nearest" | "floor" | "ceil";

export type PositionSizing =
  | {
      readonly type: "fixed-ratio";
      readonly legAContracts: number;
      readonly legBContracts: number;
      readonly rounding?: ContractRounding;
    }
  | {
      readonly type: "notional-neutral";
      readonly legAContracts: number;
      readonly rounding?: ContractRounding;
    }
  | {
      readonly type: "beta-neutral";
      readonly legAContracts: number;
      readonly rounding?: ContractRounding;
    };

export interface LegCostAssumption {
  readonly bidAskHalfSpread?: number;
  readonly commission?: number;
  readonly exchangeFees?: number;
  readonly slippage?: number;
  readonly fxConversion?: number;
  readonly rollExecution?: number;
}

export interface BacktestConfig {
  readonly entryZ: number;
  readonly exitZ?: number;
  readonly maximumHoldingObservations?: number;
  readonly stopLossCommonCurrency?: number;
  readonly sizing: PositionSizing;
  readonly legAMultiplier: number;
  readonly legBMultiplier: number;
  readonly legACurrency: Currency;
  readonly legBCurrency: Currency;
  readonly commonCurrency: Currency;
  readonly legACosts?: LegCostAssumption;
  readonly legBCosts?: LegCostAssumption;
  readonly initialCapital?: number;
}

export interface PositionSize {
  readonly theoreticalLegAContracts: number;
  readonly theoreticalLegBContracts: number;
  readonly executableLegAContracts: number;
  readonly executableLegBContracts: number;
  readonly sizing: PositionSizing["type"];
}

function roundContracts(value: number, mode: ContractRounding): number {
  const rounded = mode === "floor" ? Math.floor(value) : mode === "ceil" ? Math.ceil(value) : Math.round(value);
  return value > 0 ? Math.max(1, rounded) : 0;
}

function fxRate(currency: Currency, common: Currency, supplied: number | undefined, date: ISODate): number {
  if (currency === common) return 1;
  if (supplied === undefined || !Number.isFinite(supplied) || supplied <= 0) {
    throw new Error(`Missing valid ${currency}/${common} FX for ${date}.`);
  }
  return supplied;
}

function commonPrices(
  observation: ActualBacktestObservation,
  config: BacktestConfig,
): {
  readonly legA: number;
  readonly legB: number;
  readonly legAFxToCommon: number;
  readonly legBFxToCommon: number;
} {
  if (
    !Number.isFinite(observation.legAPrice) ||
    !Number.isFinite(observation.legBPrice) ||
    observation.legAPrice <= 0 ||
    observation.legBPrice <= 0
  ) throw new Error(`Invalid actual contract price on ${observation.date}.`);
  const legAFxToCommon = fxRate(
    config.legACurrency,
    config.commonCurrency,
    observation.legAFxToCommon,
    observation.date,
  );
  const legBFxToCommon = fxRate(
    config.legBCurrency,
    config.commonCurrency,
    observation.legBFxToCommon,
    observation.date,
  );
  return {
    legA: observation.legAPrice * legAFxToCommon,
    legB: observation.legBPrice * legBFxToCommon,
    legAFxToCommon,
    legBFxToCommon,
  };
}

export function calculatePositionSize(
  observation: ActualBacktestObservation,
  config: BacktestConfig,
): PositionSize {
  const prices = commonPrices(observation, config);
  const legAContracts = config.sizing.legAContracts;
  if (!Number.isFinite(legAContracts) || legAContracts <= 0) throw new Error("legAContracts must be positive.");
  const legANotional = legAContracts * prices.legA * config.legAMultiplier;
  const oneLegBNotional = prices.legB * config.legBMultiplier;
  let theoreticalLegBContracts: number;
  switch (config.sizing.type) {
    case "fixed-ratio":
      theoreticalLegBContracts = config.sizing.legBContracts;
      break;
    case "notional-neutral":
      theoreticalLegBContracts = legANotional / oneLegBNotional;
      break;
    case "beta-neutral":
      if (observation.beta === undefined || !Number.isFinite(observation.beta) || observation.beta <= 0) {
        throw new Error(`A positive as-of beta is required for beta-neutral sizing (${observation.date}).`);
      }
      theoreticalLegBContracts = observation.beta * legANotional / oneLegBNotional;
      break;
  }
  if (!Number.isFinite(theoreticalLegBContracts) || theoreticalLegBContracts <= 0) {
    throw new Error("Theoretical comparator contract count must be positive.");
  }
  const rounding = config.sizing.rounding ?? "nearest";
  return {
    theoreticalLegAContracts: legAContracts,
    theoreticalLegBContracts,
    executableLegAContracts: roundContracts(legAContracts, rounding),
    executableLegBContracts: roundContracts(theoreticalLegBContracts, rounding),
    sizing: config.sizing.type,
  };
}

export interface LegExecutionCostProvenance {
  readonly contracts: number;
  readonly bidAskHalfSpread: number;
  readonly commission: number;
  readonly exchangeFees: number;
  readonly slippage: number;
  readonly fxConversion: number;
  readonly rollExecution: number;
  readonly perContract: number;
  readonly total: number;
}

export interface ExecutionCostProvenance {
  readonly commonCurrency: Currency;
  readonly includeRollExecution: boolean;
  readonly legA: LegExecutionCostProvenance;
  readonly legB: LegExecutionCostProvenance;
  readonly total: number;
}

export interface ExecutionLegProvenance {
  readonly currency: Currency;
  readonly settlementPrice: number;
  readonly fxToCommon: number;
  readonly commonPrice: number;
  readonly contractMultiplier: number;
  readonly contracts: number;
  readonly absoluteNotionalCommon: number;
}

export interface BacktestExecutionProvenance {
  readonly priceSource: "actual-settlements";
  readonly settlementDate: ISODate;
  readonly commonCurrency: Currency;
  readonly legA: ExecutionLegProvenance;
  readonly legB: ExecutionLegProvenance;
  readonly grossNotionalCommon: number;
}

function costComponent(value: number | undefined, label: string): number {
  const normalized = value ?? 0;
  if (!Number.isFinite(normalized) || normalized < 0) {
    throw new Error(`${label} cost must be finite and non-negative.`);
  }
  return normalized;
}

function legCostProvenance(
  contracts: number,
  costs: LegCostAssumption | undefined,
  includeRoll: boolean,
): LegExecutionCostProvenance {
  const bidAskHalfSpread = costComponent(costs?.bidAskHalfSpread, "bidAskHalfSpread");
  const commission = costComponent(costs?.commission, "commission");
  const exchangeFees = costComponent(costs?.exchangeFees, "exchangeFees");
  const slippage = costComponent(costs?.slippage, "slippage");
  const fxConversion = costComponent(costs?.fxConversion, "fxConversion");
  const rollExecution = includeRoll ? costComponent(costs?.rollExecution, "rollExecution") : 0;
  const perContract = bidAskHalfSpread + commission + exchangeFees + slippage + fxConversion + rollExecution;
  return {
    contracts,
    bidAskHalfSpread,
    commission,
    exchangeFees,
    slippage,
    fxConversion,
    rollExecution,
    perContract,
    total: Math.abs(contracts) * perContract,
  };
}

function executionCostProvenance(
  legAContracts: number,
  legBContracts: number,
  config: BacktestConfig,
  includeRoll: boolean,
): ExecutionCostProvenance {
  const legA = legCostProvenance(legAContracts, config.legACosts, includeRoll);
  const legB = legCostProvenance(legBContracts, config.legBCosts, includeRoll);
  return {
    commonCurrency: config.commonCurrency,
    includeRollExecution: includeRoll,
    legA,
    legB,
    total: legA.total + legB.total,
  };
}

function executionProvenance(
  observation: ActualBacktestObservation,
  legAContracts: number,
  legBContracts: number,
  config: BacktestConfig,
  prices = commonPrices(observation, config),
): BacktestExecutionProvenance {
  const legA: ExecutionLegProvenance = {
    currency: config.legACurrency,
    settlementPrice: observation.legAPrice,
    fxToCommon: prices.legAFxToCommon,
    commonPrice: prices.legA,
    contractMultiplier: config.legAMultiplier,
    contracts: legAContracts,
    absoluteNotionalCommon: Math.abs(legAContracts) * prices.legA * config.legAMultiplier,
  };
  const legB: ExecutionLegProvenance = {
    currency: config.legBCurrency,
    settlementPrice: observation.legBPrice,
    fxToCommon: prices.legBFxToCommon,
    commonPrice: prices.legB,
    contractMultiplier: config.legBMultiplier,
    contracts: legBContracts,
    absoluteNotionalCommon: Math.abs(legBContracts) * prices.legB * config.legBMultiplier,
  };
  return {
    priceSource: "actual-settlements",
    settlementDate: observation.date,
    commonCurrency: config.commonCurrency,
    legA,
    legB,
    grossNotionalCommon: legA.absoluteNotionalCommon + legB.absoluteNotionalCommon,
  };
}

export interface BacktestEvent {
  readonly type: "open" | "close";
  readonly date: ISODate;
  readonly episodeId: string;
  readonly legAContract: string;
  readonly legBContract: string;
  readonly signal: number | null;
  readonly signalAsOf: ISODate | null;
  readonly legAContracts: number;
  readonly legBContracts: number;
  readonly cost: number;
  readonly execution: BacktestExecutionProvenance;
  readonly costProvenance: ExecutionCostProvenance;
  readonly reason: "entry-threshold" | "mean-reversion" | "time-stop" | "stop-loss" | "mapping-change" | "end-of-data";
}

export interface BacktestTrade {
  readonly episodeId: string;
  readonly entryDate: ISODate;
  readonly exitDate: ISODate;
  readonly direction: "long-spread" | "short-spread";
  readonly legAContracts: number;
  readonly legBContracts: number;
  readonly grossPnl: number;
  readonly costs: number;
  readonly netPnl: number;
  readonly holdingObservations: number;
  readonly mae: number;
  readonly mfe: number;
  readonly exitReason: BacktestEvent["reason"];
}

export interface DailyBacktestPoint {
  readonly date: ISODate;
  readonly episodeId: string;
  readonly grossPnl: number;
  readonly costs: number;
  readonly netPnl: number;
  readonly cumulativePnl: number;
  readonly strategyReturnIndex: number;
}

export interface BacktestMetrics {
  readonly cumulativePnl: number;
  readonly sharpe: number | null;
  readonly sortino: number | null;
  readonly maxDrawdown: number;
  readonly winRate: number | null;
  readonly profitFactor: number | null;
  readonly numberOfTrades: number;
  readonly averageHoldingPeriod: number | null;
  readonly mae: number | null;
  readonly mfe: number | null;
  /** Absolute gross notional of every open and close execution / initial capital. */
  readonly turnover: number;
  readonly estimatedTransactionCosts: number;
}

export interface BacktestResult {
  readonly scope: "actual-contract-backtest";
  readonly priceSource: "actual-settlements";
  readonly rollMethod: "explicit-close-open";
  readonly signalBoundaryRule: "recompute-per-episode-no-carry";
  readonly executionRule: "next-eligible-settlement-after-signal-as-of";
  readonly events: readonly BacktestEvent[];
  readonly trades: readonly BacktestTrade[];
  readonly daily: readonly DailyBacktestPoint[];
  readonly metrics: BacktestMetrics;
}

interface OpenPosition {
  readonly episodeId: string;
  readonly entryDate: ISODate;
  readonly direction: BacktestTrade["direction"];
  readonly legAContracts: number;
  readonly legBContracts: number;
  readonly entryCost: number;
  grossPnl: number;
  costs: number;
  holdingObservations: number;
  mae: number;
  mfe: number;
}

function validateObservation(observation: ActualBacktestObservation, contractYear: number): void {
  if (observation.isForwardFilled === true) {
    throw new Error(`Forward-filled holiday prices are forbidden (${observation.date}).`);
  }
  if (compareISODate(observation.signalAsOf, observation.date) > 0) {
    throw new Error(`Signal as-of timestamp is in the future (${observation.date}).`);
  }
  if (observation.signal !== null && !Number.isFinite(observation.signal)) {
    throw new Error(`Signal must be finite or null (${observation.date}).`);
  }
  if ((observation.baselineContractYears ?? []).some((year) => year >= contractYear)) {
    throw new Error(`Seasonal baseline contains current/future contract year (${observation.date}).`);
  }
}

function requirePriorSignalForExecution(
  signalObservation: ActualBacktestObservation,
  executionObservation: ActualBacktestObservation,
): void {
  if (compareISODate(signalObservation.signalAsOf, executionObservation.date) >= 0) {
    throw new Error(
      `Execution must use the next eligible settlement strictly after signal as-of (${executionObservation.date}).`,
    );
  }
}

function shouldMeanRevertExit(position: OpenPosition, signal: number, exitZ: number): boolean {
  return position.direction === "short-spread" ? signal <= exitZ : signal >= -exitZ;
}

function summarizeMetrics(
  daily: readonly DailyBacktestPoint[],
  trades: readonly BacktestTrade[],
  totalCosts: number,
  initialCapital: number,
  turnover: number,
): BacktestMetrics {
  const returns = daily.map((point) => point.netPnl / initialCapital);
  const standardDeviation = sampleStandardDeviation(returns);
  const downside = returns.filter((value) => value < 0);
  const downsideDeviation = sampleStandardDeviation(downside);
  const averageReturn = mean(returns);
  const winners = trades.filter((trade) => trade.netPnl > 0);
  const grossProfit = winners.reduce((sum, trade) => sum + trade.netPnl, 0);
  const grossLoss = Math.abs(trades.filter((trade) => trade.netPnl < 0).reduce((sum, trade) => sum + trade.netPnl, 0));
  let peak = 100;
  let maxDrawdown = 0;
  for (const point of daily) {
    peak = Math.max(peak, point.strategyReturnIndex);
    maxDrawdown = Math.min(maxDrawdown, point.strategyReturnIndex - peak);
  }
  return {
    cumulativePnl: daily.at(-1)?.cumulativePnl ?? 0,
    sharpe: Number.isFinite(standardDeviation) && standardDeviation > 0 ? averageReturn / standardDeviation * Math.sqrt(252) : null,
    sortino: Number.isFinite(downsideDeviation) && downsideDeviation > 0 ? averageReturn / downsideDeviation * Math.sqrt(252) : null,
    maxDrawdown,
    winRate: trades.length > 0 ? winners.length / trades.length : null,
    profitFactor: grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? Number.POSITIVE_INFINITY : null,
    numberOfTrades: trades.length,
    averageHoldingPeriod: trades.length > 0 ? mean(trades.map((trade) => trade.holdingObservations)) : null,
    mae: trades.length > 0 ? mean(trades.map((trade) => trade.mae)) : null,
    mfe: trades.length > 0 ? mean(trades.map((trade) => trade.mfe)) : null,
    turnover,
    estimatedTransactionCosts: totalCosts,
  };
}

export function runActualContractBacktest(
  episodes: readonly ActualBacktestEpisode[],
  config: BacktestConfig,
): BacktestResult {
  if (!Number.isFinite(config.entryZ) || config.entryZ <= 0) throw new Error("entryZ must be positive.");
  if (config.legAMultiplier <= 0 || config.legBMultiplier <= 0) throw new Error("Contract multipliers must be positive.");
  const initialCapital = config.initialCapital ?? 1_000_000;
  const exitZ = config.exitZ ?? 0;
  const events: BacktestEvent[] = [];
  const trades: BacktestTrade[] = [];
  const daily: DailyBacktestPoint[] = [];
  let cumulativePnl = 0;
  let totalCosts = 0;
  let turnoverNotional = 0;
  let previousEpisodeLastDate: ISODate | undefined;
  const episodeIds = new Set<string>();

  episodes.forEach((episode, episodeIndex) => {
    if (episode.kind !== "actual-episode" || episode.source !== "actual-settlements" || episode.backAdjusted !== false) {
      throw new Error("Backtest accepts only unadjusted actual contract episodes.");
    }
    if (episodeIds.has(episode.episodeId)) {
      throw new Error(`Duplicate backtest episode id: ${episode.episodeId}.`);
    }
    episodeIds.add(episode.episodeId);
    if (new Set(episode.observations.map((observation) => observation.date)).size !== episode.observations.length) {
      throw new Error(`Duplicate date in episode ${episode.episodeId}.`);
    }
    episode.observations.forEach((observation) => parseISODate(observation.date));
    for (let index = 1; index < episode.observations.length; index += 1) {
      const previous = episode.observations[index - 1];
      const current = episode.observations[index];
      if (previous && current && compareISODate(previous.date, current.date) >= 0) {
        throw new Error(`Observations must be strictly date-ordered in ${episode.episodeId}.`);
      }
    }
    const observations = episode.observations.filter((observation) => observation.eligible !== false);
    const firstDate = observations[0]?.date;
    if (
      firstDate !== undefined &&
      previousEpisodeLastDate !== undefined &&
      compareISODate(previousEpisodeLastDate, firstDate) >= 0
    ) {
      throw new Error("Backtest episodes must be strictly chronological and non-overlapping.");
    }
    const lastDate = observations.at(-1)?.date;
    if (lastDate !== undefined) previousEpisodeLastDate = lastDate;
    let position: OpenPosition | null = null;
    let previousPrices: { readonly legA: number; readonly legB: number } | null = null;
    let pendingSignalObservation: ActualBacktestObservation | null = null;

    const closePosition = (
      observation: ActualBacktestObservation,
      reason: BacktestTrade["exitReason"],
      day: { gross: number; costs: number },
      signalObservation: ActualBacktestObservation | null,
    ): void => {
      if (!position) return;
      const includeRoll = reason === "mapping-change";
      const costProvenance = executionCostProvenance(
        position.legAContracts,
        position.legBContracts,
        config,
        includeRoll,
      );
      const execution = executionProvenance(
        observation,
        position.legAContracts,
        position.legBContracts,
        config,
      );
      const closeCost = costProvenance.total;
      position.costs += closeCost;
      day.costs += closeCost;
      totalCosts += closeCost;
      turnoverNotional += execution.grossNotionalCommon;
      events.push({
        type: "close",
        date: observation.date,
        episodeId: episode.episodeId,
        legAContract: episode.legAContract,
        legBContract: episode.legBContract,
        signal: signalObservation?.signal ?? null,
        signalAsOf: signalObservation?.signalAsOf ?? null,
        legAContracts: position.legAContracts,
        legBContracts: position.legBContracts,
        cost: closeCost,
        execution,
        costProvenance,
        reason,
      });
      trades.push({
        episodeId: episode.episodeId,
        entryDate: position.entryDate,
        exitDate: observation.date,
        direction: position.direction,
        legAContracts: position.legAContracts,
        legBContracts: position.legBContracts,
        grossPnl: position.grossPnl,
        costs: position.costs,
        netPnl: position.grossPnl - position.costs,
        holdingObservations: position.holdingObservations,
        mae: position.mae,
        mfe: position.mfe,
        exitReason: reason,
      });
      position = null;
    };

    observations.forEach((observation, observationIndex) => {
      validateObservation(observation, episode.contractYear);
      const prices = commonPrices(observation, config);
      const day = { gross: 0, costs: 0 };
      let exitedThisObservation = false;
      const holdingObservationsBeforeExecution = position?.holdingObservations ?? 0;
      const netPnlBeforeExecution = position ? position.grossPnl - position.costs : 0;

      if (position && previousPrices) {
        const gross =
          position.legAContracts * config.legAMultiplier * (prices.legA - previousPrices.legA) +
          position.legBContracts * config.legBMultiplier * (prices.legB - previousPrices.legB);
        position.grossPnl += gross;
        position.holdingObservations += 1;
        position.mae = Math.min(position.mae, position.grossPnl - position.costs);
        position.mfe = Math.max(position.mfe, position.grossPnl - position.costs);
        day.gross += gross;
      }

      if (position && pendingSignalObservation) {
        requirePriorSignalForExecution(pendingSignalObservation, observation);
        let exitReason: BacktestTrade["exitReason"] | null = null;
        if (
          pendingSignalObservation.signal !== null &&
          shouldMeanRevertExit(position, pendingSignalObservation.signal, exitZ)
        ) exitReason = "mean-reversion";
        else if (
          config.maximumHoldingObservations !== undefined &&
          holdingObservationsBeforeExecution >= config.maximumHoldingObservations
        ) exitReason = "time-stop";
        else if (
          config.stopLossCommonCurrency !== undefined &&
          netPnlBeforeExecution <= -config.stopLossCommonCurrency
        ) exitReason = "stop-loss";
        if (exitReason) {
          closePosition(observation, exitReason, day, pendingSignalObservation);
          exitedThisObservation = true;
        }
      }

      const isLast = observationIndex === observations.length - 1;
      if (
        !position &&
        !exitedThisObservation &&
        !isLast &&
        pendingSignalObservation !== null &&
        pendingSignalObservation.signal !== null &&
        Math.abs(pendingSignalObservation.signal) >= config.entryZ
      ) {
        requirePriorSignalForExecution(pendingSignalObservation, observation);
        const sizingObservation = {
          ...observation,
          ...(pendingSignalObservation.beta === undefined
            ? { beta: undefined }
            : { beta: pendingSignalObservation.beta }),
        };
        const size = calculatePositionSize(sizingObservation, config);
        const direction = pendingSignalObservation.signal > 0 ? "short-spread" : "long-spread";
        const directionSign = direction === "long-spread" ? 1 : -1;
        const legAContracts = directionSign * size.executableLegAContracts;
        const legBContracts = -directionSign * size.executableLegBContracts;
        const costProvenance = executionCostProvenance(
          legAContracts,
          legBContracts,
          config,
          false,
        );
        const execution = executionProvenance(
          observation,
          legAContracts,
          legBContracts,
          config,
          prices,
        );
        const entryCost = costProvenance.total;
        day.costs += entryCost;
        totalCosts += entryCost;
        turnoverNotional += execution.grossNotionalCommon;
        position = {
          episodeId: episode.episodeId,
          entryDate: observation.date,
          direction,
          legAContracts,
          legBContracts,
          entryCost,
          grossPnl: 0,
          costs: entryCost,
          holdingObservations: 0,
          mae: -entryCost,
          mfe: -entryCost,
        };
        events.push({
          type: "open",
          date: observation.date,
          episodeId: episode.episodeId,
          legAContract: episode.legAContract,
          legBContract: episode.legBContract,
          signal: pendingSignalObservation.signal,
          signalAsOf: pendingSignalObservation.signalAsOf,
          legAContracts,
          legBContracts,
          cost: entryCost,
          execution,
          costProvenance,
          reason: "entry-threshold",
        });
      }

      if (position && isLast) {
        const hasLaterEligibleEpisode = episodes
          .slice(episodeIndex + 1)
          .some((candidate) => candidate.observations.some((value) => value.eligible !== false));
        closePosition(
          observation,
          hasLaterEligibleEpisode ? "mapping-change" : "end-of-data",
          day,
          null,
        );
      }

      const netPnl = day.gross - day.costs;
      cumulativePnl += netPnl;
      daily.push({
        date: observation.date,
        episodeId: episode.episodeId,
        grossPnl: day.gross,
        costs: day.costs,
        netPnl,
        cumulativePnl,
        strategyReturnIndex: 100 * (1 + cumulativePnl / initialCapital),
      });
      previousPrices = prices;
      pendingSignalObservation = observation;
    });
  });

  return {
    scope: "actual-contract-backtest",
    priceSource: "actual-settlements",
    rollMethod: "explicit-close-open",
    signalBoundaryRule: "recompute-per-episode-no-carry",
    executionRule: "next-eligible-settlement-after-signal-as-of",
    events,
    trades,
    daily,
    metrics: summarizeMetrics(daily, trades, totalCosts, initialCapital, turnoverNotional / initialCapital),
  };
}
